import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {


  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as TypeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Computer as InterfaceIcon,
} from '@mui/icons-material';
import { auth } from '../../../config/firebase';

/**
 * Cabeçalhos com o token do utilizador.
 *
 * As rotas `/v1/admin/services` deixaram de ser públicas — tinham 0 de 17 rotas
 * protegidas, e um POST sem token criava registos. Fechado o servidor, todos os
 * ecrãs que falavam por `fetch` cru têm de passar a enviar o token.
 */
async function cabecalhos(): Promise<Record<string, string>> {
  const base: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    await auth.authStateReady();
    const utilizador = auth.currentUser;
    if (utilizador) base.Authorization = `Bearer ${await utilizador.getIdToken()}`;
  } catch (erro) {
    console.error('[admin] não foi possível obter o token:', erro);
  }
  return base;
}

interface ServiceType {
  id: string;
  codigo: string;
  nome: string;
  descr: string;
  ui: string;
  transactionCode: string;
  referenceLabel?: string;
  referenceHint?: string;
  showEntityField?: string;
  keyboardType?: string;
}

const KEYBOARD_OPTIONS = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Numérico' },
  { value: 'phone', label: 'Telefone' },
];

const TX_CODE_OPTIONS = [
  { value: '1', label: '1 - Compra (E-Commerce, Eventos, Donativos)', color: 'primary' as const },
  { value: '2', label: '2 - Pagamento de Serviço (Facturas)', color: 'warning' as const },
  { value: '3', label: '3 - Recarga', color: 'secondary' as const },
];

const API_URL = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';

const ServiceTypes: React.FC = () => {
  const [types, setTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<ServiceType | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    desig: '',
    descr: '',
    ui: 'N',
    transactionCode: '1',
    referenceLabel: 'Referência',
    referenceHint: '',
    showEntityField: 'N',
    keyboardType: 'text',
  });
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  };

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/v1/admin/services/types`, { headers: await cabecalhos() });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTypes(data.data || []);
        }
      } else {
        throw new Error('Failed to fetch service types');
      }
    } catch (error) {
      console.error('Error fetching service types:', error);
      showToast('Erro ao carregar tipos de serviço', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const filteredTypes = types.filter(type =>
    searchTerm === '' ||
    type.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (type.descr && type.descr.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (type?: ServiceType) => {
    if (type) {
      setSelectedType(type);
      setFormData({
        codigo: type.codigo,
        desig: type.nome,
        descr: type.descr || '',
        ui: type.ui || 'N',
        transactionCode: type.transactionCode || '1',
        referenceLabel: type.referenceLabel || 'Referência',
        referenceHint: type.referenceHint || '',
        showEntityField: type.showEntityField || 'N',
        keyboardType: type.keyboardType || 'text',
      });
    } else {
      setSelectedType(null);
      setFormData({
        codigo: '',
        desig: '',
        descr: '',
        ui: 'N',
        transactionCode: '1',
        referenceLabel: 'Referência',
        referenceHint: '',
        showEntityField: 'N',
        keyboardType: 'text',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedType(null);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.desig) {
      showToast('Preencha os campos obrigatórios', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = selectedType
        ? `${API_URL}/v1/admin/services/types/${selectedType.id}`
        : `${API_URL}/v1/admin/services/types`;

      const method = selectedType ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: await cabecalhos(),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        showToast(
          selectedType ? 'Tipo de serviço atualizado com sucesso' : 'Tipo de serviço criado com sucesso',
          'success'
        );
        handleCloseEdit();
        fetchTypes();
      } else {
        showToast(data.error || 'Erro ao guardar tipo de serviço', 'error');
      }
    } catch (error) {
      console.error('Error saving service type:', error);
      showToast('Erro ao guardar tipo de serviço', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: ServiceType) => {
    if (!window.confirm(`Tem certeza que deseja eliminar o tipo de serviço "${type.nome}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/admin/services/types/${type.id}`, {
        method: 'DELETE',
        headers: await cabecalhos(),
      });

      const data = await response.json();

      if (data.success) {
        showToast('Tipo de serviço eliminado com sucesso', 'success');
        fetchTypes();
      } else {
        showToast(data.error || 'Erro ao eliminar tipo de serviço', 'error');
      }
    } catch (error) {
      console.error('Error deleting service type:', error);
      showToast('Erro ao eliminar tipo de serviço', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Tipos de Serviço
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure os tipos de serviço disponíveis na plataforma
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTypes}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            Novo Tipo
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TypeIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{types.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Tipos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InterfaceIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {types.filter(t => t.ui === 'S').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Com Interface</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Pesquisar por código, designação ou descrição..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Types Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={100}>Código</TableCell>
                    <TableCell>Designação</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell width={200} align="center">Transaction Code SISP</TableCell>
                    <TableCell>Label Referência</TableCell>
                    <TableCell>Teclado</TableCell>
                    <TableCell width={80} align="center">Entidade</TableCell>
                    <TableCell width={80} align="center">Interface</TableCell>
                    <TableCell width={100} align="center">Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum tipo de serviço encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTypes
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((type) => (
                        <TableRow key={type.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                              {type.codigo}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {type.nome}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{
                              maxWidth: 350,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {type.descr || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {(() => {
                              const opt = TX_CODE_OPTIONS.find(o => o.value === type.transactionCode);
                              return <Chip size="small" label={opt?.label || type.transactionCode || '1'} color={opt?.color || 'default'} />;
                            })()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {type.referenceLabel || 'Referência'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {type.referenceHint || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={KEYBOARD_OPTIONS.find(o => o.value === type.keyboardType)?.label || type.keyboardType || 'Texto'} variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={type.showEntityField === 'S' ? 'Sim' : 'Não'} color={type.showEntityField === 'S' ? 'info' : 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={type.ui === 'S' ? 'Tem interface de utilizador' : 'Sem interface'}>
                              <Chip
                                size="small"
                                label={type.ui === 'S' ? 'Sim' : 'Não'}
                                color={type.ui === 'S' ? 'success' : 'default'}
                                icon={type.ui === 'S' ? <ActiveIcon /> : <InactiveIcon />}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(type)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(type)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTypes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypeIcon />
          {selectedType ? 'Editar Tipo de Serviço' : 'Novo Tipo de Serviço'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Código"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 5 }}
                required
                disabled={!!selectedType}
                helperText={selectedType ? 'Código não pode ser alterado' : 'Max 5 caracteres'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Designação"
                value={formData.desig}
                onChange={(e) => setFormData({ ...formData, desig: e.target.value })}
                inputProps={{ maxLength: 45 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.descr}
                onChange={(e) => setFormData({ ...formData, descr: e.target.value })}
                inputProps={{ maxLength: 150 }}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Transaction Code SISP"
                value={formData.transactionCode}
                onChange={(e) => setFormData({ ...formData, transactionCode: e.target.value })}
                helperText="Define como o SISP processa este tipo de pagamento"
              >
                {TX_CODE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Interface de Utilizador"
                value={formData.ui}
                onChange={(e) => setFormData({ ...formData, ui: e.target.value })}
                helperText="Permite acesso à interface de criação"
              >
                <MenuItem value="S">Sim</MenuItem>
                <MenuItem value="N">Não</MenuItem>
              </TextField>
            </Grid>

            {/* Parametrização do Formulário de Pagamento */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5 }}>
                Parametrização do Formulário de Pagamento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Define como o formulário aparece no frontend e app mobile
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Label do Campo Referência"
                value={formData.referenceLabel}
                onChange={(e) => setFormData({ ...formData, referenceLabel: e.target.value })}
                inputProps={{ maxLength: 100 }}
                helperText='Ex: "Número", "Referência", "Apólice"'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Placeholder (Hint)"
                value={formData.referenceHint}
                onChange={(e) => setFormData({ ...formData, referenceHint: e.target.value })}
                inputProps={{ maxLength: 255 }}
                helperText='Ex: "Insira o número de telemóvel"'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Tipo de Teclado"
                value={formData.keyboardType}
                onChange={(e) => setFormData({ ...formData, keyboardType: e.target.value })}
                helperText="Teclado que aparece no mobile"
              >
                {KEYBOARD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Mostrar Campo Entidade"
                value={formData.showEntityField}
                onChange={(e) => setFormData({ ...formData, showEntityField: e.target.value })}
                helperText="Mostra campo extra para código de entidade"
              >
                <MenuItem value="S">Sim</MenuItem>
                <MenuItem value="N">Não</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.codigo || !formData.desig}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {saving ? 'A guardar...' : selectedType ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceTypes;
